import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiPost, apiPatch, apiGet, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'

const getDashboards = async (token, userId, params) => {
  const url = `users/${userId}/dashboards`

  const data = await apiGet({ url, params, token })
  return data
}

export function useDashboards({ params, options }) {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)
  const queryFn = () => getDashboards(token, id, params)
  const queryKey = ['dashboards', params]

  return useQuery(queryKey, queryFn, {
    ...options
  })
}

const getDashboard = async (token, id, params) => {
  const url = `dashboards/${id}`

  const data = await apiGet({ url, params, token })
  return data
}

export function useDashboard({ params, id, options }) {
  const token = useContext(AuthContext)
  const queryFn = () => getDashboard(token, id, params)
  const queryKey = [`dashboards/${id}`]

  return useQuery(queryKey, queryFn, {
    ...options
  })
}

const createDashboard = async (token, params, id) => {
  const objectName = 'dashboard'
  const url = `users/${id}/dashboards`

  const data = await apiPost({ url, token, objectName, params })
  return data
}

export function useCreateDashboard({ params, options }) {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)

  return useMutation(
    () => {
      return createDashboard(token, params, id)
    },
    { ...options }
  )
}

const updateDashboard = async (token, params, id) => {
  const objectName = 'dashboard'
  const url = `dashboards/${id}`

  const data = await apiPatch({ url, token, objectName, params })
  return data
}

export function useUpdateDashboard({ id, options }) {
  const token = useContext(AuthContext)

  return useMutation(
    (params) => {
      return updateDashboard(token, params, id)
    },
    { ...options }
  )
}

const destroyDashboard = async (token, id) => {
  const url = `dashboards/${id}`

  await apiDelete({ url, token })
}

export function useDestroyDashboard({ id, options }) {
  const token = useContext(AuthContext)

  return useMutation(
    () => {
      return destroyDashboard(token, id)
    },
    { ...options }
  )
}

const duplicateDashboard = async (token, id) => {
  const objectName = 'dashboard_id'
  const url = `dashboards/${id}/duplications`

  const data = await apiPost({ url, token, objectName, params: id })
  return data
}

export function useDuplicateDashboard({ id, options }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    () => {
      return duplicateDashboard(token, id)
    },
    {
      ...options,
      onSuccess: (data) => {
        // Invalidate the dashboards query to refresh the list
        queryClient.invalidateQueries('dashboards')
        queryClient.invalidateQueries('current-user')

        // Call the original onSuccess if provided
        if (options?.onSuccess) {
          options.onSuccess(data)
        }
      }
    }
  )
}